import React, { useEffect, useState } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	Radio,
	Box,
} from '@mui/material';
import { AntSwitch } from '../../../components/page/Vendor/Vendor-ui/antSwith';

interface PermissionItem {
	create: boolean;
	edit: boolean;
	read: boolean;
	delete: boolean;
}

interface Permissions {
	[key: string]: PermissionItem | undefined;
}

interface MenuTableProps {
	permissions: Permissions;
	onChange: (updatedPermissions: Permissions) => void;
	vendorDisabel?: boolean;
}

const MenuTable: React.FC<MenuTableProps> = ({ permissions = {}, onChange, vendorDisabel }) => {
	const [localPermissions, setLocalPermissions] = useState<Permissions>({});

	// ✅ Sync incoming props → local state
	useEffect(() => {
		if (permissions && Object.keys(permissions).length > 0) {
			setLocalPermissions(permissions);
		}
	}, [permissions]);

	// Toggle a single permission
	const handleTogglePermission = (menu: string, action: keyof PermissionItem) => {
		if (!localPermissions[menu]) return;

		const updated: Permissions = {
			...localPermissions,
			[menu]: {
				...localPermissions[menu]!,
				[action]: !localPermissions[menu]![action],
			} as PermissionItem,
		};
		setLocalPermissions(updated);
		onChange(updated);
	};

	// Toggle all permissions in a row
	const handleToggleRow = (menu: string) => {
		if (!localPermissions[menu]) return;

		const allEnabled = Object.values(localPermissions[menu]!).every(Boolean);
		const updated: Permissions = {
			...localPermissions,
			[menu]: {
				create: !allEnabled,
				edit: !allEnabled,
				read: !allEnabled,
				delete: !allEnabled,
			} as PermissionItem,
		};
		setLocalPermissions(updated);
		onChange(updated);
	};

	// Toggle ALL permissions in the table
	const handleToggleAll = () => {
		const allEnabled = Object.values(localPermissions).every(
			(menu) => menu && Object.values(menu).every(Boolean)
		);

		const updated: Permissions = {};
		for (const key in localPermissions) {
			updated[key] = {
				create: !allEnabled,
				edit: !allEnabled,
				read: !allEnabled,
				delete: !allEnabled,
			} as PermissionItem;
		}
		setLocalPermissions(updated);
		onChange(updated);
	};

	// Helpers
	const isRowAllChecked = (menu: string) =>
		localPermissions[menu] && Object.values(localPermissions[menu]!).every(Boolean);

	const isAllChecked = Object.values(localPermissions).every(
		(menu) => menu && Object.values(menu).every(Boolean)
	);

	// Empty state
	if (!localPermissions || Object.keys(localPermissions).length === 0) {
		return <Typography>No permissions data available.</Typography>;
	}

	return (
		<TableContainer component={Paper}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell sx={{ p: 0 }}>
							{/* Global "All" toggle */}
							<Radio
								checked={Boolean(isAllChecked)}
								onClick={handleToggleAll}
								color="primary"
								sx={{ '& .MuiSvgIcon-root': { fontSize: 22 } }}
								disabled={vendorDisabel}
							/>
						</TableCell>
						<TableCell sx={{ p: 0 }}>
							<strong>Menu</strong>
						</TableCell>
						{(['create', 'edit', 'read', 'delete'] as (keyof PermissionItem)[]).map(
							(action) => (
								<TableCell key={action} sx={{ px: { xs: 2, sm: 0 } }}>
									<Box>
										<strong>
											{action.charAt(0).toUpperCase() + action.slice(1)}
										</strong>
									</Box>
								</TableCell>
							)
						)}
					</TableRow>
				</TableHead>
				<TableBody>
					{Object.entries(localPermissions).map(([menu, actions]) =>
						actions ? (
							<TableRow key={menu}>
								<TableCell sx={{ p: 0 }}>
									{/* Row "All" toggle */}
									<Radio
										checked={Boolean(isRowAllChecked(menu))}
										onClick={() => handleToggleRow(menu)}
										color="primary"
										sx={{ '& .MuiSvgIcon-root': { fontSize: 22 } }}
										disabled={vendorDisabel}
									/>
								</TableCell>

								<TableCell sx={{ textTransform: 'capitalize', p: 0 }}>
									{menu}
								</TableCell>

								{(
									['create', 'edit', 'read', 'delete'] as (keyof PermissionItem)[]
								).map((action) => (
									<TableCell
										key={action}
										padding="none"
										sx={{ px: { xs: 2, sm: 0 } }}
									>
										<AntSwitch
											disabled={vendorDisabel}
											checked={Boolean(actions[action])}
											onChange={() => handleTogglePermission(menu, action)}
											inputProps={{ 'aria-label': `${menu}-${action}` }}
										/>
									</TableCell>
								))}
							</TableRow>
						) : null
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default MenuTable;
